import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Fragment, useMemo, useState } from "react";
import appConfig from "src/config/env";
import { INVENTORY_EXPIRE, payloadFilter } from "src/constant";
import useInventoryList from "src/hooks/inventory/useInventoryList";
import useProdGroups from "src/hooks/product-groups/useProdGroups";
import useQueryParams from "src/hooks/useQueryParams";
import { v4 as uuid } from "uuid";

const sumBatchFieldAcrossModels = (productModels, field) => {
  if (!Array.isArray(productModels)) return 0;
  return productModels.reduce((acc, pm) => {
    const batches = pm?.inventoryBatches ?? [];
    return acc + batches.reduce((a, b) => a + Number(b?.[field] ?? 0), 0);
  }, 0);
};

const renderBatchMetricColumn = (batches, field) => (
  <Box>
    {Array.isArray(batches) && batches.length > 0 ? (
      batches.map((batch, idx) => (
        <Typography
          key={batch?._id ?? idx}
          sx={{ fontSize: "12px", mb: "6px" }}
        >
          {batch?.[field] ?? "-"}
        </Typography>
      ))
    ) : (
      <Typography variant="body2">-</Typography>
    )}
  </Box>
);

export default function useInventoryBody({ keyword, topbarSearchList, searchBy, date, prodId, brand, warehouseId }) {
  const [option] = useQueryParams();
  const [pagi, setPagi] = useState({
    limit: 50,
    skip: 0,
  });
  const showPeriodMovement = !!(date?.fromDate && date?.toDate);
  const { data: productGroup } = useProdGroups({
    sortField: "order",
    sortOrder: "asc",
  });

  const tabIndex = Number(option?.tab);

  const {
    data: inventory,
    isLoading,
    error,
  } = useInventoryList({
    ...pagi,
    warehouse: warehouseId,
    searchField: searchBy !== 2 ? (!!searchBy ? topbarSearchList[searchBy - 1]?.value : topbarSearchList[0]?.value) : undefined,
    searchValue: !!keyword ? keyword : undefined,
    ...date,
    ...payloadFilter,
    nearExpireDate: INVENTORY_EXPIRE[option?.tab],
    product: prodId ? prodId : undefined,
    brand: brand ? brand : undefined,
    productGroup: option?.tab !== "inventory" && Number.isFinite(tabIndex) ? productGroup?.data?.[tabIndex - 1]?._id : undefined,
    sortField: "name",
    sortOrder: "asc",
  });

  const header = useMemo(() => {
    const periodHeaders = showPeriodMovement
      ? [
          { id: uuid(), title: "Tồn đầu kỳ", align: "center" },
          { id: uuid(), title: "Hàng nhập", align: "center" },
          { id: uuid(), title: "Hàng xuất", align: "center" },
        ]
      : [];
    return [
      { id: uuid(), title: "STT", align: "center" },
      { id: uuid(), title: "Sản phẩm/ Hàng hoá", align: "center" },
      { id: uuid(), title: "Số lượng nhập", align: "center" },
      { id: uuid(), title: "Tồn kho khả dụng", align: "center" },
      ...periodHeaders,
      { id: uuid(), title: "Tồn kho theo Lô SP", align: "center" },
      { id: uuid(), title: "Ngày sản xuất", align: "center" },
      { id: uuid(), title: "Ngày hết hạn", align: "center" },
      { id: uuid(), title: "Thao tác", align: "center" },
    ];
  }, [showPeriodMovement]);

  const bodyList =
    Array.isArray(inventory?.data) && inventory?.data?.length > 0
      ? inventory?.data?.map((v, index) => ({
          ...v,
          no: index + 1,
          initQty: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v.initQty ?? "-"}</Typography>,
          productItem: (
            <Box>
              {/* PRODUCT */}
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  sx={{
                    minHeight: "66px",
                    minWidth: "66px",
                    maxHeight: "66px",
                    maxWidth: "66px",
                    border: "1px solid #cccccc",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    background: !!v?.product?.image?.src
                      ? `url(${appConfig.API_FILE}/${v?.product?.image?.src}) center / cover no-repeat`
                      : "url(/img/broke-img.png) center / cover no-repeat",
                  }}
                ></Box>
                <Box textAlign="left">
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                  >
                    {v?.product?.name ?? "-"}
                  </Typography>
                  <Typography variant="body2">{v?.product?.code ? `${v?.product?.code}` : "-"}</Typography>
                </Box>
              </Box>
            </Box>
          ),
          stock: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{v?.qty - v.lockQty ?? "-"}</Typography>,
          ...(showPeriodMovement && {
            periodOpenQty: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{sumBatchFieldAcrossModels(v?.productModels, "openQty")}</Typography>,
            periodImportedQty: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{sumBatchFieldAcrossModels(v?.productModels, "importedQty")}</Typography>,
            periodExportedQty: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{sumBatchFieldAcrossModels(v?.productModels, "exportedQty")}</Typography>,
          }),
          stockBatch: "",
          importDate: "",
          exp: "",
          includesRow: v?.productModels?.map((val) => ({
            col: <></>,
            product: (
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  sx={{
                    width: "46px",
                    height: "46px",
                    ml: "20px",
                    border: "1px solid #cccccc",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    background: !!val?.image?.src ? `url(${appConfig.API_FILE}/${val?.image?.src}) center / cover no-repeat` : "url(/img/broke-img.png) center / cover no-repeat",
                  }}
                ></Box>
                <Box textAlign="left">
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                  >
                    {val?.name ?? "-"}
                  </Typography>
                  <Typography variant="body2">{val?.sku ? `SKU: ${val?.sku}` : "-"}</Typography>
                </Box>
              </Box>
            ),
            col1: (
              <Typography variant="body2">
                {val?.initQty ?? 0} <br />
                {val?.createdAt ? dayjs(val?.createdAt).format("DD-MM-YYYY") : ""}
              </Typography>
            ),
            stock: <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>{val?.qty - val?.lockQty}</Typography>,
            ...(showPeriodMovement && {
              periodOpenQty: renderBatchMetricColumn(val?.inventoryBatches, "openQty"),
              periodImportedQty: renderBatchMetricColumn(val?.inventoryBatches, "importedQty"),
              periodExportedQty: renderBatchMetricColumn(val?.inventoryBatches, "exportedQty"),
            }),
            stockBatch: (
              <Box>
                {val?.inventoryBatches?.map((value) => {
                  const expDate = dayjs(value?.batch?.exp);
                  const today = dayjs();
                  const daysUntilExp = expDate.diff(today, "day");
                  return (
                    <Fragment key={value?._id}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          mb: "6px",
                          fontWeight: daysUntilExp < 365 ? "bold" : "normal",
                          color: daysUntilExp < 365 ? "error.main" : "text.primary",
                          background: daysUntilExp < 365 ? "var(--Remove)" : "var(--Delivered)",
                        }}
                      >
                        <strong>({value?.qty}) </strong>
                        {value?.batch?.code}
                      </Typography>
                    </Fragment>
                  );
                })}
              </Box>
            ),
            mfg: (
              <Box>
                {val?.inventoryBatches?.map((value, index) => {
                  const expDate = dayjs(value?.batch?.exp);
                  const today = dayjs();
                  const daysUntilExp = expDate.diff(today, "day");
                  return (
                    <Fragment key={value?._id}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          mb: "6px",
                          fontWeight: daysUntilExp < 365 ? "bold" : "normal",
                          color: daysUntilExp < 365 ? "error.main" : "text.primary",
                          background: daysUntilExp < 365 ? "var(--Remove)" : "var(--Delivered)",
                        }}
                      >
                        {dayjs(value?.batch?.mfg).format("DD/MM/YYYY")}
                      </Typography>
                    </Fragment>
                  );
                })}
              </Box>
            ),
            exp: (
              <Box>
                {val?.inventoryBatches?.map((value, index) => {
                  const expDate = dayjs(value?.batch?.exp);
                  const today = dayjs();
                  const daysUntilExp = expDate.diff(today, "day");

                  return (
                    <Fragment key={value?._id}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          mb: "6px",
                          fontWeight: daysUntilExp < 365 ? "bold" : "normal",
                          color: daysUntilExp < 365 ? "error.main" : "text.primary",
                          background: daysUntilExp < 365 ? "var(--Remove)" : "var(--Delivered)",
                        }}
                      >
                        {expDate.format("DD/MM/YYYY")}
                      </Typography>
                    </Fragment>
                  );
                })}
              </Box>
            ),
            col2: <></>,
          })),
        }))
      : [];

  const sortData = useMemo(() => {
    const base = ["no", "productItem", "initQty", "stock"];
    if (showPeriodMovement) {
      base.push("periodOpenQty", "periodImportedQty", "periodExportedQty");
    }
    base.push("stockBatch", "importDate", "exp");
    return base;
  }, [showPeriodMovement]);

  return {
    header,
    isLoading,
    error,
    bodyList,
    sortData,
    setPagi,
    pagi,
    total: inventory?.count,
  };
}
